import { Controller, useForm } from 'react-hook-form';
import { useAuthModalStore } from '../../../store/auth-modal';
import { ErrorMessage } from './ErrorMessage';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { UsaStates } from 'usa-states';
import Select from 'react-select';
import { useAdrValidation } from '../address/adrModalHook';
import { AddressCardSm } from '../address/addressCardSm';
import { PhoneInputWithRef } from '../address/PhoneInputWithRef.';
import {
  emailMaxLength,
  inputMaxLength,
  zipMaxLength
} from '../../../utils/validation/validation-rules';
import { yupResolver } from '@hookform/resolvers/yup';
import { registerSchema } from '../../../utils/validation/registration-schema';
import cn from 'classnames';
import { useRouter } from 'next/router';
import ButtonWithLoading from '../../../components/common/ButtonWithLoading';

export function Register({
  onSubmitEmit,
  textButton = 'Create account',
  phoneDisable = false,
  newUserTryLogin = false,
  emailDisable = false
}) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    watch,
    reset
  } = useForm({ resolver: yupResolver(registerSchema) });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 575);
  const [fromCheckout, setFromCheckout] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { formData, ...state } = useAuthModalStore();
  const [ValidModal, modalData, closeModal, validateAdr] = useAdrValidation();
  const formInputValues = watch();
  const router = useRouter();

  const statesSelectValues = useMemo(() => {
    const usStates = new UsaStates();
    return usStates.states.map(state => ({
      value: state.name,
      label: state.name
    }));
  }, []);

  const updateMedia = () => {
    setIsMobile(window.innerWidth <= 575);
  };

  const chooseAdrHandle = useCallback(async (adrData = {}) => {
    const fixData = {
      ...formInputValues,
      state: formInputValues?.state?.value,
      ...adrData,
      zipcode: Object.keys(adrData).length
        ? adrData.zip
        : formInputValues.zipcode,
      address1: Object.keys(adrData).length
        ? adrData.street_1
        : formInputValues.address1,
      address2: Object.keys(adrData).length
        ? adrData.street_2
        : formInputValues.address2
    };

    onSubmitEmit(fixData);
    setFromCheckout(false)
    closeModal()
  }, [formInputValues, onSubmitEmit, closeModal]);

  const onSubmit = useCallback(async data => {
    setIsLoading(true);

    const adrObj = {
      address: {
        city: data.city,
        state: data.state.value,
        zip: data.zipcode,
        street1: data.address1,
        street2: data.address2 || ''
      }
    };
    const isValid = await validateAdr(adrObj);
    if (isValid) {
      await chooseAdrHandle()
    }

    setTimeout(() => {
      setIsLoading(false);
    }, 2000);
  },[validateAdr, chooseAdrHandle])

  const customSelectStyles = {
    dropdownIndicator: provided => ({
      ...provided,
      paddingRight: '16px',
      paddingTop: '11px'
    }),
    indicatorSeparator: () => ({
      display: 'none'
    })
  };

  useEffect(() => {
    window.addEventListener('resize', updateMedia);
    return () => window.removeEventListener('resize', updateMedia);
  }, []);

  useEffect(() => {
    const data = {
      ...formData,
      state: formData.state && { value: formData.state, label: formData.state }
    };
    reset(data);
  }, [formData, reset]);

  useEffect(() => {
    if (router?.query?.fromCheckout) {
      setFromCheckout(true);
      router.push(
        {
          pathname: router.pathname,
          query: router.query,
        },
        undefined,
        { shallow: true }
      );
    }
  }, []);

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        {fromCheckout && (
          <ErrorMessage
            bg={'error'}
            message={
              'To create an order, you need to fill in the required fields'
            }
          />
        )}
        {newUserTryLogin && (
          <ErrorMessage
            bg={'error'}
            message={
              'We couldn\'t find your account. If you have already signed up, go back and use your original verification method to login. Otherwise complete the form to continue.'
            }
          />
        )}
        {state.message && (
          <ErrorMessage
            bg={state.statusBg}
            message={state.message}
          />
        )}
        <div className="row">
          {/* First Name */}
          <div
            className="col-md-6"
            style={{ paddingRight: '8px' }}
          >
            <div className="form-group">
              <label htmlFor="acc-name">
                First Name <span className="required"> *</span>
              </label>
              <input
                {...register('firstName')}
                autoComplete="given-name"
                className={cn('form-control', {
                  'form-control-error': errors.firstName
                })}
                id="acc-name"
                maxLength={inputMaxLength}
                type="text"
              />
              {errors.firstName && (
                <p className={'text-danger'}>{errors.firstName.message}</p>
              )}
            </div>
          </div>
          {/* Last Name */}
          <div
            className="col-md-6"
            style={{ paddingLeft: '8px' }}
          >
            <div className="form-group">
              <label htmlFor="acc-lastname">
                Last Name <span className="required"> *</span>
              </label>
              <input
                {...register('lastName')}
                autoComplete="family-name"
                className={cn('form-control', {
                  'form-control-error': errors.lastName
                })}
                id="acc-lastname"
                maxLength={inputMaxLength}
                type="text"
              />
              {errors.lastName && (
                <p className={'text-danger'}>{errors.lastName.message}</p>
              )}
            </div>
          </div>
        </div>

        <div className="row">
          {/* Company Name */}
          <div className="col-md-12">
            <div className="form-group">
              <label htmlFor="acc-company">Company Name (Optional)</label>
              <input
                {...register('company')}
                autoComplete="organization"
                className={cn('form-control', {
                  'form-control-error': errors.company
                })}
                id="acc-company"
                maxLength={inputMaxLength}
                type="text"
              />
              {errors.company && (
                <p className={'text-danger'}>{errors.company.message}</p>
              )}
            </div>
          </div>
        </div>

        <div className="row">
          {/* Address Line 1 */}
          <div className="col-md-12">
            <div className="form-group">
              <label htmlFor="acc-address1">
                Address Line 1 <span className="required"> *</span>
              </label>
              <input
                {...register('address1', {
                  required: 'Address Line 1 is required'
                })}
                autoComplete="address-line1"
                className={cn('form-control', {
                  'form-control-error': errors.address1
                })}
                id="acc-address1"
                maxLength={inputMaxLength}
                type="text"
              />
              {errors.address1 && (
                <p className={'text-danger'}>{errors.address1.message}</p>
              )}
            </div>
          </div>
        </div>

        <div className="row">
          {/* Address Line 2 */}
          <div className="col-md-12">
            <div className="form-group">
              <label htmlFor="acc-address2">Address Line 2</label>
              <input
                {...register('address2')}
                autoComplete="address-line2"
                className={cn('form-control', {
                  'form-control-error': errors.address2
                })}
                id="acc-address2"
                maxLength={inputMaxLength}
                type="text"
              />
              {errors.address2 && (
                <p className={'text-danger'}>{errors.address2.message}</p>
              )}
            </div>
          </div>
        </div>

        <div className="row">
          {/* Suburb/City */}
          <div className="col-md-4">
            <div className="form-group">
              <label htmlFor="acc-city">
                City <span className="required"> *</span>
              </label>
              <input
                {...register('city', {
                  required: 'City is required'
                })}
                autoComplete="address-level2"
                className={cn('form-control', {
                  'form-control-error': errors.city
                })}
                id="acc-city"
                maxLength={inputMaxLength}
                type="text"
              />
              {errors.city && (
                <p className={'text-danger'}>{errors.city.message}</p>
              )}
            </div>
          </div>
          {/* State/Province */}
          <div className="col-md-4">
            <div className="form-group">
              <label htmlFor="acc-state">
                State <span className="required"> *</span>
              </label>
              <Controller
                control={control}
                name="state"
                render={({ field }) => (
                  <Select
                    className={cn('form-control-select', {
                      'form-control-error': errors.state
                    })}
                    styles={customSelectStyles}
                    {...field}
                    inputId="acc-state"
                    options={statesSelectValues}
                  />
                )}
              />
              {errors.state && (
                <p className={'text-danger'}>{errors.state.message}</p>
              )}
            </div>
          </div>
          {/* Zip/Postcode */}
          <div className="col-md-4">
            <div className="form-group">
              <label htmlFor="acc-zipcode">
                Zip Code<span className="required"> *</span>
              </label>
              <input
                {...register('zipcode', {
                  required: 'Zip Code is required'
                })}
                autoComplete="postal-code"
                className={cn('form-control', {
                  'form-control-error': errors.zipcode
                })}
                id="acc-zipcode"
                maxLength={zipMaxLength}
                type="text"
              />
              {errors.zipcode && (
                <p className={'text-danger'}>{errors.zipcode.message}</p>
              )}
            </div>
          </div>
        </div>

        <div className="row">
          {/* Phone */}
          <div className="col-md-6">
            <div className="form-group">
              <label htmlFor="acc-name">
                Phone <span className="required"> *</span>
              </label>
              <Controller
                control={control}
                name="phone"
                render={({ field }) => (
                  <PhoneInputWithRef
                    {...field}
                    country={'us'}
                    countryCodeEditable={false}
                    disableDropdown
                    disabled={phoneDisable}
                    id="acc-phone"
                    inputClass={cn('form-control phone-input', {
                      'form-control-error': errors.phone
                    })}
                    onlyCountries={['us', 'ua']}
                    placeholder={'PHONE #'}
                  />
                )}
              />
              {errors.phone && (
                <p className={'text-danger'}>{errors.phone.message}</p>
              )}
            </div>
          </div>
          {/* Email Address */}
          <div className="col-md-6">
            <div className="form-group">
              <label htmlFor="acc-email">
                Email Address <span className="required"> *</span>
              </label>
              <input
                {...register('email')}
                autoComplete="email"
                className={cn('form-control', {
                  'form-control-error': errors.email
                })}
                disabled={emailDisable}
                id="acc-email"
                maxLength={emailMaxLength}
                type="email"

                // placeholder="editor@gmail.com"
                style={{cursor: emailDisable ? 'not-allowed' : 'text'}}
              />
              {errors.email && (
                <p className={'text-danger'}>{errors.email.message}</p>
              )}
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="mt-3 d-flex justify-content-center">
          <ButtonWithLoading
            className="btn w-sm-100 btn-dark mr-0 btn-md btn-register"
            loading={isLoading}
            type="submit"
          >
            {textButton}
          </ButtonWithLoading>
        </div>
      </form>

      {/* Address Validation Modal */}
      <ValidModal>
        {isMobile ? (
          <div className="adr-modal-mobile">
            <h1 className="adr-modal-mobile__title">
              {'The address you\'ve entered can not match postal service records'}
            </h1>
            <div className="adr-modal-mobile__block">
              <AddressCardSm
                addressData={{
                  ...formInputValues,
                  first_name: formInputValues.firstName,
                  last_name: formInputValues.lastName,
                  street_1: formInputValues.address1,
                  street_2: formInputValues.address2,
                  zip: formInputValues.zipcode
                }}
              />
              <button
                className={'btn btn-light btn-md adr-modal-mobile__btn'}
                onClick={() => chooseAdrHandle()}
              >
                USE THIS ADDRESS
              </button>
            </div>

            <span className="adr-modal-mobile__or">OR</span>

            {modalData?.isValid && (
              <div className="adr-modal-mobile__block">
                <AddressCardSm
                  addressData={{
                    ...modalData.addressValidated,
                    first_name: formInputValues.firstName,
                    last_name: formInputValues.lastName
                  }}
                />
                <button
                  className={'btn btn-dark btn-md adr-modal-mobile__btn'}
                  onClick={() => chooseAdrHandle(modalData.addressValidated)}
                >
                  USE THIS ADDRESS
                </button>
              </div>
            )}
            {!modalData?.isValid && (
              <div className="adr-modal-mobile__block">
                <span className={'text-center text-dark mb-1'}>
                  We can not automatically confirm this address. Please check it
                  carefully.
                </span>
                <button
                  className={'btn btn-dark btn-md adr-modal-mobile__btn'}
                  onClick={closeModal}
                >
                  BACK
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className={'row d-flex flex-column align-content-center'}>
            <span className={'text-center text-dark font-weight-bold m-2'}>
              {'The address you\'ve entered can not match postal service records'}
            </span>
            <div className="col-md-10 d-flex ml-5">
              <AddressCardSm
                addressData={{
                  ...formInputValues,
                  first_name: formInputValues.firstName,
                  last_name: formInputValues.lastName,
                  street_1: formInputValues.address1,
                  street_2: formInputValues.address2,
                  zip: formInputValues.zipcode
                }}
              />
              {modalData?.isValid && (
                <AddressCardSm
                  addressData={{
                    ...modalData.addressValidated,
                    first_name: formInputValues.firstName,
                    last_name: formInputValues.lastName
                  }}
                />
              )}
              {!modalData?.isValid && (
                <div
                  className={
                    'address-card w-100 d-flex flex-md-column align-items-center mt-3'
                  }
                >
                  <span className={'text-center text-dark mb-2'}>
                    We can not automatically confirm this address. Please check
                    it carefully.
                  </span>
                  {/* <div className={'card-divider align-self-center mt-1'}></div> */}
                </div>
              )}
            </div>
            <div
              className={
                'mt-2 d-flex justify-content-center align-items-center'
              }
            >
              <button
                className={'use-address-btn btn-light mr-5'}
                onClick={() => chooseAdrHandle()}
              >
                USE THIS ADDRESS
              </button>
              <span
                className={
                  'text-center text-uppercase text-dark font-weight-bold'
                }
              >
                or
              </span>
              {modalData?.addressValidated ? (
                <button
                  className={'use-address-btn btn-dark ml-5 '}
                  onClick={() => chooseAdrHandle(modalData.addressValidated)}
                >
                  USE THIS ADDRESS
                </button>
              ) : (
                <button
                  className={'btn w-25 btn-dark ml-5 btn-md'}
                  onClick={closeModal}
                >
                  BACK
                </button>
              )}
            </div>
          </div>
        )}
      </ValidModal>
    </>
  );
}
